import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Avatar, Stack } from "@mui/material";
import CompetitionPodium from "./CompetitionPodium";
import { listRowBackground, listRowBorderColor } from "../../../utils/siteListTheme";
import FinalNightVoteButton from "./FinalNightVoteButton";
import { formatTotalScore } from "../../../utils/finalNightScoring";

function useCountUp(target, duration = 1100, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId;
    let startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(tid);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, duration, delay]);
  return value;
}

export function mapFinalNightRow(row, index = 0) {
  const publicVotes = Number(row.public_votes) || 0;
  const carryoverVotes = Number(row.carryover_votes) || 0;
  const fnSessionVotes = Math.max(0, publicVotes - carryoverVotes);
  const name = row.full_name || "Contestant";
  const parts = name.trim().split(/\s+/);
  const firstName = parts[0] || name;
  const lastName = parts.slice(1).join(" ");

  return {
    id: row.contestant_id,
    contestant_id: row.contestant_id,
    rank: row.rank || index + 1,
    voteCount: publicVotes,
    vote_count: publicVotes,
    public_votes: publicVotes,
    carryover_votes: carryoverVotes,
    fn_session_votes: fnSessionVotes,
    judge_avg: Number(row.judge_avg) || 0,
    total_score: Number(row.total_score) || 0,
    admin_adjustment: Number(row.admin_adjustment) || 0,
    profile_image: row.profile_image,
    full_name: name,
    category: row.category,
    is_eliminated: Boolean(row.is_eliminated),
    contestant: {
      profile_image: row.profile_image,
      user: {
        firstName,
        lastName,
        profile_img: row.profile_image,
      },
    },
  };
}

function FinalNightPositionRow({
  row,
  position,
  index,
  maxVotes,
  showVote,
  voting,
  voted = false,
  onVote,
  showCarryover = false,
}) {
  const rowRef = useRef(null);
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const stagger = Math.min(index * 55, 550);
  const eliminated = Boolean(row.is_eliminated);
  const isTop3 = !eliminated && position <= 3;
  const isFirst = !eliminated && position === 1;
  const voteCount = row.voteCount ?? 0;
  const animatedVotes = useCountUp(
    voteCount,
    1100,
    active ? stagger + 180 : 9999999
  );
  const avatar =
    row.contestant?.profile_image || row.contestant?.user?.profile_img;
  const name =
    row.full_name ||
    `${row.contestant?.user?.firstName || ""} ${row.contestant?.user?.lastName || ""}`.trim() ||
    "Contestant";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setActive(true), stagger);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    if (rowRef.current) observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, [stagger]);

  const borderColor = eliminated
    ? "rgba(65,68,102,0.45)"
    : listRowBorderColor({ isFirst, isTop3, hovered });

  const bg = eliminated
    ? "rgba(52,56,98,0.45)"
    : listRowBackground({ isFirst, isTop3, hovered });

  const judgeLine =
    row.judge_avg > 0
      ? `Judge ${row.judge_avg}/10`
      : "Awaiting judge scores";
  const voteLine = `${voteCount} public vote${voteCount === 1 ? "" : "s"}`;
  const adjLine =
    row.admin_adjustment !== 0
      ? ` · Adj ${row.admin_adjustment > 0 ? "+" : ""}${row.admin_adjustment}`
      : "";
  const totalScore = formatTotalScore(row.total_score);

  return (
    <Box
      ref={rowRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        opacity: active ? (eliminated ? 0.55 : 1) : 0,
        transform: active
          ? eliminated
            ? "translateY(0)"
            : hovered
              ? "translateY(-2px)"
              : "translateY(0)"
          : "translateY(14px)",
        filter: eliminated ? "grayscale(0.85)" : "none",
        transition:
          "opacity 0.4s ease, transform 0.28s ease, box-shadow 0.28s ease",
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 1.25,
        background: bg,
        border: `1px solid ${borderColor}`,
        boxShadow: isFirst
          ? "0 10px 28px rgba(240,199,94,0.12)"
          : isTop3
            ? "0 8px 22px rgba(0,0,0,0.25)"
            : hovered
              ? "0 10px 24px rgba(239,54,78,0.12)"
              : "none",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={{ xs: 1.2, sm: 2 }}
        sx={{ p: { xs: 1.2, sm: 1.6 } }}
      >
        <Box
          sx={{
            width: 40,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.3,
          }}
        >
          {isFirst ? (
            <Typography sx={{ fontSize: 22, lineHeight: 1, color: "#F0C75E" }}>
              ♛
            </Typography>
          ) : null}
          <Typography
            sx={{
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: isTop3 ? 17 : 14,
              color: eliminated
                ? "rgba(255,255,255,0.25)"
                : isFirst
                  ? "#F0C75E"
                  : isTop3
                    ? "#E8C4A8"
                    : "rgba(255,255,255,0.4)",
            }}
          >
            {eliminated ? "—" : position}
          </Typography>
        </Box>

        <Avatar
          src={avatar || ""}
          sx={{
            width: { xs: 44, sm: 52 },
            height: { xs: 44, sm: 52 },
            flexShrink: 0,
            border: isFirst
              ? "2px solid #F0C75E"
              : isTop3
                ? "2px solid rgba(232,196,168,0.7)"
                : "2px solid rgba(255,255,255,0.12)",
          }}
        >
          {name[0]}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              noWrap
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontFamily: "Montserrat, sans-serif",
                fontSize: { xs: 14, sm: 15 },
              }}
            >
              {name}
            </Typography>
            {row.category ? (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.75)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                {row.category}
              </Box>
            ) : null}
            {eliminated ? (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "rgba(255,255,255,0.06)",
                  color: "rgba(255,255,255,0.45)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                Eliminated
              </Box>
            ) : isTop3 ? (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: isFirst
                    ? "rgba(240,199,94,0.2)"
                    : "rgba(232,196,168,0.15)",
                  color: isFirst ? "#F0C75E" : "#E8C4A8",
                  border: `1px solid ${
                    isFirst
                      ? "rgba(240,199,94,0.4)"
                      : "rgba(232,196,168,0.35)"
                  }`,
                }}
              >
                Top {position}
              </Box>
            ) : null}
          </Stack>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.5)",
              fontSize: 12,
              mt: 0.4,
              fontWeight: 600,
            }}
          >
            {judgeLine}
            {" · "}
            {voteLine}
            {adjLine}
            {showCarryover && row.carryover_votes > 0 ? (
              <>
                {" · "}
                {row.carryover_votes} from competition
                {row.fn_session_votes > 0
                  ? ` + ${row.fn_session_votes} tonight`
                  : ""}
              </>
            ) : null}
          </Typography>
          <Box
            sx={{
              mt: 0.8,
              height: 3,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.08)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: active
                  ? `${Math.min((voteCount / maxVotes) * 100, 100)}%`
                  : 0,
                background: isFirst
                  ? "linear-gradient(90deg, #FFE08A, #F0C75E)"
                  : isTop3
                    ? "linear-gradient(90deg, #E8C4A8, #C9956C)"
                    : "linear-gradient(90deg, #ef364e, #c43a4e)",
                transition: "width 1.1s cubic-bezier(0.25,0.46,0.45,0.94)",
              }}
            />
          </Box>
        </Box>

        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                color: isFirst ? "#F0C75E" : isTop3 ? "#E8C4A8" : "#fff",
                fontWeight: 800,
                fontFamily: "Montserrat, sans-serif",
                fontSize: 17,
                lineHeight: 1,
              }}
            >
              {totalScore}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.35)",
                fontSize: 10,
                fontWeight: 600,
                mt: 0.35,
              }}
            >
              {animatedVotes} votes
            </Typography>
          </Box>
          {showVote && !eliminated ? (
            <FinalNightVoteButton
              loading={voting}
              voted={voted}
              onClick={() => onVote?.(row.contestant_id)}
              compact
            />
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export default function FinalNightLeaderboard({
  rows = [],
  showVote = false,
  votingId = null,
  votedContestantId = null,
  onVote,
  categoryTitle = "Final Night",
  roundOrder = null,
  judgesPercent = null,
  usersPercent = null,
}) {
  const mapped = useMemo(
    () => rows.map((r, i) => mapFinalNightRow(r, i)),
    [rows]
  );

  const sorted = useMemo(() => {
    return [...mapped].sort((a, b) => {
      if (a.is_eliminated !== b.is_eliminated) {
        return a.is_eliminated ? 1 : -1;
      }
      const as = a.total_score || a.voteCount;
      const bs = b.total_score || b.voteCount;
      if (bs !== as) return bs - as;
      return b.voteCount - a.voteCount;
    });
  }, [mapped]);

  const activeRows = useMemo(
    () => sorted.filter((r) => !r.is_eliminated),
    [sorted]
  );

  const maxVotes = useMemo(() => {
    const top = activeRows[0]?.voteCount ?? 0;
    return Math.max(top, 1);
  }, [activeRows]);

  const showCarryover = roundOrder === 1;

  return (
    <Box sx={{ px: { xs: 0.5, md: 1 }, pb: 2 }}>
      <CompetitionPodium
        rows={activeRows}
        categoryTitle={categoryTitle}
        finalNightScoring
        judgesPercent={judgesPercent}
        usersPercent={usersPercent}
      />
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 800,
          fontFamily: "Montserrat, sans-serif",
          fontSize: 18,
          mb: 1.5,
        }}
      >
        Position
      </Typography>
      {sorted.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed rgba(255,255,255,0.14)",
            borderRadius: 3,
            p: 5,
            textAlign: "center",
            color: "rgba(255,255,255,0.5)",
          }}
        >
          No contestants ranked yet.
        </Box>
      ) : (
        sorted.map((r, i) => (
          <FinalNightPositionRow
            key={r.contestant_id || i}
            row={r}
            position={i + 1}
            index={i}
            maxVotes={maxVotes}
            showVote={showVote}
            voting={votingId === r.contestant_id}
            voted={votedContestantId === r.contestant_id}
            onVote={onVote}
            showCarryover={showCarryover}
          />
        ))
      )}
    </Box>
  );
}
